import i18n from '../i18n';

/**
 * Device timezone offset for API payloads (minutes east of UTC).
 * Example: IST → 330. JS `Date.getTimezoneOffset()` is the opposite sign
 * (UTC − local), so we negate it to match .NET / existing app convention.
 */
const getTimezoneOffsetMinutes = (): number => -new Date().getTimezoneOffset();

/**
 * Format a date to a human-readable string
 * @param {Date|string|number} date
 * @param {string} [format='MMM DD, YYYY']
 * @returns {string}
 */
const formatDate = (
  date: Date | string | number,
  format = 'MMM DD, YYYY',
): string => {
  // Use parseToDate for correct UTC → local conversion. The old code
  // stripped the time/timezone and built a local-midnight Date from the
  // date part only, which showed the wrong date near UTC midnight.
  const d = date instanceof Date ? date : parseToDate(date);
  if (!d || isNaN(d.getTime())) return '';

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const fullMonths = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const pad = (n: number): string => String(n).padStart(2, '0');
  const h12 = hours % 12 || 12;
  const ampm = hours < 12 ? 'AM' : 'PM';

  return format.replace(/MMMM|MMM|MM|DD|D|YYYY|YY|HH|hh|mm|ss|A/g, (match) => {
    switch (match) {
      case 'MMMM': return fullMonths[month];
      case 'MMM': return months[month];
      case 'MM': return pad(month + 1);
      case 'DD': return pad(day);
      case 'D': return String(day);
      case 'YYYY': return String(year);
      case 'YY': return String(year).slice(-2);
      case 'HH': return pad(hours);
      case 'hh': return pad(h12);
      case 'mm': return pad(minutes);
      case 'ss': return pad(seconds);
      case 'A': return ampm;
      default: return match;
    }
  });
};

/** UI display pattern for Date of Birth (e.g. Aug 05, 2000). */
const DOB_DISPLAY_FORMAT = 'MMM DD, YYYY';

/** Display Date of Birth as Month Day, Year across the app (UI only). */
const formatDateOfBirth = (date: Date | string | number | null | undefined): string => {
  if (date == null || date === '') return '';
  // DOB is a calendar date, so drop any time component first: a value like
  // "2000-08-05T00:00:00" read as an instant rolls back a day west of UTC.
  return formatDate(toDateOnlyString(date) || date, DOB_DISPLAY_FORMAT);
};

/**
 * Calendar date → `YYYY-MM-DD` using **local** year/month/day.
 * DOB is a calendar date, not an instant — never use `toISOString()` here
 * (UTC conversion shifts the day in non-UTC timezones).
 */
const toDateOnlyString = (
  value: Date | string | number | null | undefined,
): string => {
  if (value == null || value === '') return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Prefer the YYYY-MM-DD calendar prefix so we never Date-parse a DOB
    // instant and shift the day by timezone. Covers date-only strings,
    // "2000-08-05T00:00:00…", and legacy toISOString() DOB values.
    const datePrefix = trimmed.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePrefix)) {
      return datePrefix;
    }
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a calendar date string as local midnight.
 * - `YYYY-MM-DD` → local date (no UTC interpretation)
 * - other strings → native `Date` parse (prefer `toDateOnlyString` first
 *   for DOB so the YYYY-MM-DD prefix is preserved)
 */
const parseDateOnlyString = (
  value: string | null | undefined,
): Date | null => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, day] = raw.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const RELATIVE_TIME_PATTERN =
  /^(just now|\d+\s+seconds? ago|\d+\s+minutes? ago|\d+\s+hours? ago|\d+\s*(sec|secs|min|mins|hr|hrs|h|d|day|days|wk|wks|mo|mos|yr|yrs)\s*ago|yesterday)$/i;

/**
 * How to read a backend timestamp that carries no timezone designator.
 *
 * - `'utc'` — the value is UTC. Conversation endpoints behave this way
 *   (see `conversationMapper.parseUtcTimestamp`).
 * - `'local'` — the API already converted the value to the caller's timezone
 *   from the `timezoneOffset` we send on the request. Feed, group-post,
 *   comment and notification reads all send it (`withFeedTimezoneOffset`).
 *   Appending `Z` to those strings applies the offset a second time, which
 *   pushes recent items into the future and collapses them to "Just now".
 */
type NaiveTimestampZone = 'utc' | 'local';

/** `YYYY-MM-DD[T ]HH:mm[:ss[.fraction]]` with no timezone designator. */
const NAIVE_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/;

const buildTimestamp = (
  parts: RegExpMatchArray,
  naiveZone: NaiveTimestampZone,
): Date | null => {
  const [, year, month, day, hour, minute, second, fraction] = parts;
  const y = Number(year);
  const mo = Number(month) - 1;
  const d = Number(day);
  const h = Number(hour);
  const mi = Number(minute);
  const s = second ? Number(second) : 0;
  // .NET returns up to 7 fractional digits; JS only takes milliseconds.
  const ms = fraction ? Number(fraction.slice(0, 3).padEnd(3, '0')) : 0;

  const built =
    naiveZone === 'utc'
      ? new Date(Date.UTC(y, mo, d, h, mi, s, ms))
      : new Date(y, mo, d, h, mi, s, ms);
  return isNaN(built.getTime()) ? null : built;
};

const parseToDate = (
  date: Date | string | number,
  naiveZone: NaiveTimestampZone = 'utc',
): Date | null => {
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === 'number') {
    // Support both seconds and milliseconds epoch values.
    const ms = date < 1e12 ? date * 1000 : date;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  const raw = String(date).trim();
  if (!raw) return null;

  // Already a relative string from the API / prior formatting.
  if (RELATIVE_TIME_PATTERN.test(raw)) {
    return null;
  }

  let normalized = raw;

  // Detect whether this is a date-only string (e.g. "2000-08-05") or a
  // full timestamp with a time component. Date-only strings are calendar
  // dates (DOB) and are always LOCAL — reading them as UTC would shift the
  // day in non-UTC timezones. Timestamps follow `naiveZone`.
  const hasTimeComponent = /[\sT]\d{2}:\d{2}/.test(normalized);
  const isDateOnly = !hasTimeComponent;

  // DD-MM-YYYY[ HH:mm:ss] → YYYY-MM-DDTHH:mm:ss
  if (/^\d{2}-\d{2}-\d{4}/.test(normalized)) {
    const parts = normalized.split(/[- :T]/);
    if (parts.length >= 3) {
      normalized = `${parts[2]}-${parts[1]}-${parts[0]}${
        parts[3] ? `T${parts[3]}` : ''
      }${parts[4] ? `:${parts[4]}` : ''}${parts[5] ? `:${parts[5]}` : ''}`;
    }
  } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(normalized)) {
    // YYYY-MM-DD HH:mm:ss → ISO-like
    normalized = normalized.replace(' ', 'T');
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(normalized)) {
    // M/D/YYYY[ h:mm:ss AM/PM] e.g. "8/7/2026 5:22:01 AM"
    const m = normalized.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i,
    );
    if (m) {
      let hours = m[4] != null ? parseInt(m[4], 10) : 0;
      const minutes = m[5] != null ? parseInt(m[5], 10) : 0;
      const seconds = m[6] != null ? parseInt(m[6], 10) : 0;
      const ampm = (m[7] || '').toUpperCase();
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      const year = parseInt(m[3], 10);
      const month = parseInt(m[1], 10) - 1;
      const day = parseInt(m[2], 10);
      const parsed =
        naiveZone === 'utc'
          ? new Date(Date.UTC(year, month, day, hours, minutes, seconds))
          : new Date(year, month, day, hours, minutes, seconds);
      if (!isNaN(parsed.getTime())) return parsed;
    }
  }

  // Date-only strings (DOB, calendar dates) → parse as LOCAL, never UTC.
  if (isDateOnly && /^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const [y, mo, dy] = normalized.split('-').map(Number);
    const d = new Date(y, mo - 1, dy);
    if (!isNaN(d.getTime())) return d;
  }

  const hasExplicitZone = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(normalized);

  // Timestamp with no timezone designator → read it in the caller's declared
  // zone instead of letting the engine guess.
  if (hasTimeComponent && !hasExplicitZone) {
    const isoParts = normalized.match(NAIVE_TIMESTAMP_PATTERN);
    if (isoParts) {
      const built = buildTimestamp(isoParts, naiveZone);
      if (built) return built;
    }

    // Non-ISO shapes the engine can still parse (it treats them as local).
    if (naiveZone === 'utc') {
      const dUtc = new Date(`${normalized}Z`);
      if (!isNaN(dUtc.getTime())) return dUtc;
    }
  }

  // Fallback: native parse (handles strings with explicit timezone, and
  // date-only strings that should be local).
  const d = new Date(normalized);
  if (!isNaN(d.getTime())) return d;

  return null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Whole days between two instants on the viewer's local calendar. */
const localCalendarDaysBetween = (from: Date, to: Date): number => {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((end - start) / DAY_MS);
};

/** Calendar days back that still read as a weekday instead of a date. */
const CONTENT_WEEKDAY_WINDOW_DAYS = 3;

/**
 * Which shape a post / comment / chat timestamp takes.
 *
 * The bucket comes from the viewer's local calendar, not from elapsed
 * milliseconds: a message sent at 11:50 PM has to read as a weekday once
 * midnight passes, even though only ten minutes went by.
 */
type ContentTimestampDescriptor =
  | { kind: 'time' }
  | { kind: 'weekdayTime' }
  | { kind: 'monthDay'; withYear: boolean };

const describeContentTimestamp = (
  date: Date,
  now: Date = new Date(),
): ContentTimestampDescriptor => {
  // Clamped: a viewer clock running behind the server must not read fresh
  // content as tomorrow's and label it with a weekday.
  const days = Math.max(0, localCalendarDaysBetween(date, now));
  if (days === 0) return { kind: 'time' };
  if (days <= CONTENT_WEEKDAY_WINDOW_DAYS) return { kind: 'weekdayTime' };
  return { kind: 'monthDay', withYear: date.getFullYear() !== now.getFullYear() };
};

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * Weekday name, e.g. `Saturday`, from the catalogue rather than `Intl`.
 * The clock and month names below are fixed strings, so reading only the
 * weekday from the host locale would emit a half-translated label such as
 * `Samedi 11:59 PM` and make the output depend on the device's default locale.
 */
const formatWeekdayLong = (date: Date): string =>
  i18n.t(`dateTime.weekdays.${WEEKDAY_KEYS[date.getDay()]}`);

const formatContentDate = (date: Date, withYear: boolean): string =>
  withYear
    ? `${formatDateFromUtc(date)}, ${date.getFullYear()}`
    : formatDateFromUtc(date);

/**
 * The single timestamp label for posts, comments, chat bubbles, conversation
 * rows and notifications, in the viewer's local timezone:
 *
 *   today                   `11:59 PM`
 *   1–3 calendar days back  `Saturday 11:59 PM`
 *   older, same year        `Sep 11`
 *   older, earlier year     `Sep 11, 2025`
 */
const formatContentTimestampFromDate = (
  date: Date,
  now: Date = new Date(),
): string => {
  const descriptor = describeContentTimestamp(date, now);
  switch (descriptor.kind) {
    case 'time':
      return formatTimeFromUtc(date);
    case 'weekdayTime':
      return `${formatWeekdayLong(date)} ${formatTimeFromUtc(date)}`;
    default:
      return formatContentDate(date, descriptor.withYear);
  }
};

/**
 * Same rule for a value straight off the API.
 *
 * `naiveZone` defaults to `'local'` because feed, group-post, comment and
 * notification reads send `timezoneOffset`; conversation and chat instants are
 * UTC, so those callers pass `'utc'`. A value the API already formatted
 * (e.g. `"2 hours ago"`) carries no instant and is returned untouched.
 */
const formatContentTimestamp = (
  value: Date | string | number | null | undefined,
  naiveZone: NaiveTimestampZone = 'local',
  now: Date = new Date(),
): string => {
  if (value == null || value === '') return '';
  const d = parseToDate(value, naiveZone);
  if (!d) return typeof value === 'string' ? value : '';
  return formatContentTimestampFromDate(d, now);
};

/**
 * Chat day-separator label: the same buckets without the clock, since the
 * separator already heads a group of individually timed messages.
 */
const formatContentDayLabel = (date: Date, now: Date = new Date()): string => {
  const descriptor = describeContentTimestamp(date, now);
  switch (descriptor.kind) {
    case 'time':
      return i18n.t('chat.today');
    case 'weekdayTime':
      return formatWeekdayLong(date);
    default:
      return formatContentDate(date, descriptor.withYear);
  }
};

/**
 * Check if a date falls on the viewer's local calendar today.
 */
const isToday = (
  date: Date | string | number,
  naiveZone: NaiveTimestampZone = 'utc',
): boolean => {
  const d = date instanceof Date ? date : parseToDate(date, naiveZone);
  if (!d) return false;
  const today = new Date();
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
};

/** Viewer's local clock, e.g. "6:38 PM". */
const formatTimeFromUtc = (date: Date): string => {
  const hour12 = date.getHours() % 12 || 12;
  const ampm = date.getHours() < 12 ? 'AM' : 'PM';
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hour12}:${minutes} ${ampm}`;
};

/** Viewer's local date, e.g. "Aug 14". */
const formatDateFromUtc = (date: Date): string =>
  `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}`;

/** Local instant → UTC ISO string for APIs that expect UTC. */
const convertLocalDateTimeToUtc = (date: Date): string => date.toISOString();

/** Minimum required age for onboarding and profile (years). */
const MIN_AGE_REQUIREMENT = 18;

/**
 * Returns the maximum allowable date of birth for an 18-year-old (today minus 18 years).
 */
const getMaxDateOfBirth = (minAge = MIN_AGE_REQUIREMENT): Date => {
  const today = new Date();
  const targetYear = today.getFullYear() - minAge;
  const targetMonth = today.getMonth();
  const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const safeDay = Math.min(today.getDate(), daysInMonth);
  return new Date(targetYear, targetMonth, safeDay, 0, 0, 0, 0);
};

/**
 * Calculate completed years of age from a date of birth.
 */
const getAge = (dob: Date | string | number | null | undefined): number => {
  if (!dob) return 0;
  const d = dob instanceof Date ? dob : parseToDate(dob) ?? new Date(dob);
  if (isNaN(d.getTime())) return 0;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age--;
  }
  return age;
};

/**
 * Validates if the given date of birth meets the minimum age requirement (default: 18).
 */
const isAtLeastAge = (
  dob: Date | string | number | null | undefined,
  minAge = MIN_AGE_REQUIREMENT,
): boolean => {
  if (!dob) return false;
  return getAge(dob) >= minAge;
};

export type { NaiveTimestampZone, ContentTimestampDescriptor };

export {
  MIN_AGE_REQUIREMENT,
  getMaxDateOfBirth,
  getAge,
  isAtLeastAge,
  getTimezoneOffsetMinutes,
  formatDate,
  parseToDate,
  formatDateOfBirth,
  toDateOnlyString,
  parseDateOnlyString,
  isToday,
  describeContentTimestamp,
  formatContentTimestamp,
  formatContentTimestampFromDate,
  formatContentDayLabel,
  convertLocalDateTimeToUtc,
};
